/**
 * Static marketing pack proof — no Mermaid / agentic imports (landing perf).
 * Visual anchor for the hero: looks like a board-ready report, not abstract boxes.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

const PackPreviewMock: React.FC = () => {
  const { t } = useTranslation();

  const chips = [
    t('pages.landing.pack_preview_chip_architecture'),
    t('pages.landing.pack_preview_chip_risks'),
    t('pages.landing.pack_preview_chip_estimate'),
  ];

  return (
    <div
      className="marketing-pack-preview relative w-full max-w-md rounded-2xl border border-primary/25 bg-gradient-to-b from-gray-900/95 to-gray-950/95 p-4 sm:p-5 text-left shadow-[0_20px_50px_-12px_rgba(0,0,0,0.65),0_0_40px_-8px_rgba(22,163,74,0.25)]"
      data-testid="landing-pack-preview"
      aria-label={t('pages.landing.pack_preview_label')}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-primary-light">
          {t('pages.landing.pack_preview_label')}
        </p>
        <span className="text-[10px] font-medium text-gray-500 tabular-nums">v1 · board</span>
      </div>

      {/* Architecture sketch */}
      <div className="rounded-xl border border-white/10 bg-black/40 p-4 mb-3" aria-hidden>
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col items-center gap-1.5 min-w-0 flex-1">
            <div className="h-9 w-full max-w-[4.5rem] rounded-lg border border-primary/50 bg-primary/20 shadow-[0_0_12px_rgba(74,222,128,0.15)]" />
            <span className="text-[9px] text-primary-light/90 font-medium truncate w-full text-center">API</span>
          </div>
          <div className="flex-1 h-px bg-gradient-to-r from-primary/60 via-primary/30 to-white/20 self-center mb-4" />
          <div className="flex flex-col items-center gap-1.5 min-w-0 flex-1">
            <div className="h-9 w-full max-w-[4.5rem] rounded-lg border border-white/20 bg-white/5" />
            <span className="text-[9px] text-gray-400 font-medium truncate w-full text-center">Domain</span>
          </div>
          <div className="flex-1 h-px bg-gradient-to-r from-white/20 to-emerald-500/40 self-center mb-4" />
          <div className="flex flex-col items-center gap-1.5 min-w-0 flex-1">
            <div className="h-9 w-full max-w-[4.5rem] rounded-lg border border-emerald-500/30 bg-emerald-500/10" />
            <span className="text-[9px] text-emerald-400/90 font-medium truncate w-full text-center">Data</span>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-1.5">
            <div className="h-1 w-8 rounded bg-white/20 mb-1.5" />
            <div className="h-1 w-5 rounded bg-white/10" />
          </div>
          <div className="rounded-md border border-primary/30 bg-primary/10 px-2 py-1.5">
            <div className="h-1 w-8 rounded bg-primary/50 mb-1.5" />
            <div className="h-1 w-6 rounded bg-primary/30" />
          </div>
          <div className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-1.5">
            <div className="h-1 w-7 rounded bg-white/20 mb-1.5" />
            <div className="h-1 w-4 rounded bg-white/10" />
          </div>
        </div>
      </div>

      {/* Risks + estimate teaser rows */}
      <div className="space-y-2 mb-4" aria-hidden>
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />
          <div className="flex-1 h-1.5 rounded bg-amber-400/25" />
          <div className="w-10 h-1.5 rounded bg-amber-400/15" />
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
          <span className="h-1.5 w-1.5 rounded-full bg-primary-light shrink-0" />
          <div className="flex-1 h-1.5 rounded bg-primary/30" />
          <div className="w-14 h-1.5 rounded bg-primary/20" />
        </div>
      </div>

      <ul className="flex flex-wrap gap-2">
        {chips.map((label) => (
          <li
            key={label}
            className="rounded-md border border-primary/25 bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary-light"
          >
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PackPreviewMock;
